/**
 * 봇(투자 세션) 상태를 관리하는 Provider입니다.
 *
 * 제공 데이터:
 * - taFunctions: 기술 분석 함수 목록
 * - sessionConfig: 현재 세션 설정 (실행 상태, 투자 모드)
 *
 * @remarks
 * tRPC 구독을 통해 세션 설정 변경을 실시간으로 감지합니다.
 * taFunctions는 최초 1회만 조회합니다 (staleTime: Infinity).
 */
import type { TaFunction, MakeOptional, SessionConfig, TaFunctionId } from '@bit-box/common';
import { InvestMode, RunningMode } from '@bit-box/common';
import { createContextProvider } from '../common/create-context';
import { trpc } from '../common/trpc';
import { useState, useEffect } from 'react';
import isEqual from 'lodash-es/isEqual';

type SessionConfigWithOptionalSessionDuration = MakeOptional<SessionConfig, 'duration'>;

const defaultSession: SessionConfigWithOptionalSessionDuration = {
  running: RunningMode.STOPPED, // running: false,
  investMode: InvestMode.SIMULATION,
  duration: undefined,
};

const [useBotContext, Provider] = createContextProvider<{
  taFunctions?: Map<TaFunctionId, TaFunction>;
  isPending?: boolean;
  error?: unknown;
  sessionConfig: SessionConfigWithOptionalSessionDuration;
}>({
  taFunctions: new Map(),
  isPending: true,
  error: undefined,
  sessionConfig: defaultSession,
});

/**
 * 봇(투자 세션) 상태를 관리하는 Provider.
 * 기술 분석 함수 목록과 세션 설정(실행 상태, 투자 모드)을 제공합니다.
 * tRPC 구독을 통해 세션 설정 변경을 실시간으로 감지합니다.
 */
const BotProvider = ({ children }: { children: React.ReactNode }) => {
  // 기술 분석 함수. staleTime: Infinity 는 최초 1회만 요청하기 위함
  const tafQuery = trpc.invest.taFunctions.useQuery(undefined, { staleTime: Infinity });
  // investSvc 의 실행 중 여부
  const sessionQuery = trpc.invest.sessionConfig.useQuery();

  const [sessionConfig, setSessionConfig] = useState<SessionConfigWithOptionalSessionDuration>({
    id: sessionQuery.data?.id,
    running: sessionQuery.data?.running || RunningMode.STOPPED,
    investMode: sessionQuery.data?.investMode || InvestMode.SIMULATION,
    duration: sessionQuery.data?.duration,
  });

  useEffect(() => {
    setSessionConfig(prev => {
      const newConfig = {
        id: sessionQuery.data?.id,
        running: sessionQuery.data?.running || RunningMode.STOPPED,
        investMode: sessionQuery.data?.investMode || InvestMode.SIMULATION,
        duration: sessionQuery.data?.duration,
      };
      if (isEqual(prev, newConfig)) {
        return prev;
      }
      return newConfig;
    });
  }, [sessionQuery.data]);

  // 봇 실행 중 여부
  trpc.invest.onSessionConfigChanged.useSubscription(undefined, {
    onData(data: { old: SessionConfig; new: SessionConfig }) {
      console.log('onSessionConfigChanged', data);
      setSessionConfig(prev => {
        if (isEqual(prev, data.new)) {
          return prev;
        }
        return data.new;
      });
    },
  });

  // console.log(`BotProvider. sessionConfig`, sessionConfig);

  return (
    <Provider
      value={{
        taFunctions: tafQuery.data,
        isPending: tafQuery.isPending || sessionQuery.isPending,
        error: tafQuery.error || sessionQuery.error,
        sessionConfig,
      }}
    >
      {children}
    </Provider>
  );
};

export { useBotContext, BotProvider };
